/* eslint-disable no-undef */
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons'
import { PageContainer } from '@ant-design/pro-layout'
import { makeStyles } from '@material-ui/styles'
import { Button, Result, Spin } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'

import dimorderApi from '@/libs/api/dimorderApi'
import { useSelector } from '@/redux'

import { KEETA_OAUTH_STATE_KEY } from './utils'

const STATUS = {
  PROCESSING: 'processing',
  SUCCESS: 'success',
  ERROR: 'error',
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
})

export default function KeetaCallback(props) {
  const { t } = useTranslation()
  const classes = useStyles(props)
  const location = useLocation()
  const history = useHistory()
  const activeMerchant = useSelector((state) => state.merchant.activeMerchant)

  const [status, setStatus] = useState(STATUS.PROCESSING)
  const [errorMessage, setErrorMessage] = useState('')
  // 避免 React StrictMode 或重新渲染重複呼叫 exchange
  const handledRef = useRef(false)

  useEffect(() => {
    if (handledRef.current) return
    if (!activeMerchant?.id) return
    handledRef.current = true

    const params = new URLSearchParams(location.search)
    const code = params.get('code')
    const state = params.get('state')
    const errorParam = params.get('error')
    const savedState = localStorage.getItem(KEETA_OAUTH_STATE_KEY)
    console.log(
      `[state] Received OAuth callback with code: ${code}, state: ${state}, error: ${errorParam}, savedState: ${savedState}`,
    ) // for debugging;

    localStorage.removeItem(KEETA_OAUTH_STATE_KEY)

    if (errorParam) {
      setStatus(STATUS.ERROR)
      setErrorMessage(t('page.deliveryPlatform.keeta.callback.userCancelled'))
      return
    }

    if (!state || state !== savedState) {
      setStatus(STATUS.ERROR)
      setErrorMessage(t('page.deliveryPlatform.keeta.callback.stateMismatch'))
      return
    }

    if (!code) {
      setStatus(STATUS.ERROR)
      setErrorMessage(t('page.deliveryPlatform.keeta.callback.missingCode'))
      return
    }

    const authorize = async () => {
      try {
        await dimorderApi.instance(activeMerchant.id)?.authorizeKeeta({ authCode: code })
        setStatus(STATUS.SUCCESS)
        setTimeout(() => {
          history.replace(`/delivery-platform?code=${code}`)
        }, 1500)
      } catch (error) {
        setStatus(STATUS.ERROR)
        console.error(error)
        setErrorMessage(t('page.deliveryPlatform.keeta.callback.exchangeFailed'))
      }
    }

    authorize()
  }, [activeMerchant?.id])

  const backToMain = () => history.replace('/delivery-platform')

  return (
    <PageContainer ghost header={{ title: t('page.deliveryPlatform.title') }}>
      <div className={classes.container}>
        {status === STATUS.PROCESSING && (
          <div style={{ textAlign: 'center' }}>
            <Spin size='large' />
            <div style={{ marginTop: 16 }}>
              {t('page.deliveryPlatform.keeta.callback.processing')}
            </div>
          </div>
        )}
        {status === STATUS.SUCCESS && (
          <Result
            icon={<CheckCircleFilled style={{ color: '#52c41a' }} />}
            status='success'
            title={t('page.deliveryPlatform.keeta.callback.successTitle')}
            subTitle={t('page.deliveryPlatform.keeta.callback.successSubTitle')}
          />
        )}
        {status === STATUS.ERROR && (
          <Result
            icon={<CloseCircleFilled style={{ color: '#ff4d4f' }} />}
            status='error'
            title={t('page.deliveryPlatform.keeta.callback.errorTitle')}
            subTitle={errorMessage}
            extra={
              <Button type='primary' onClick={backToMain}>
                {t('page.deliveryPlatform.keeta.callback.backToMain')}
              </Button>
            }
          />
        )}
      </div>
    </PageContainer>
  )
}
